import {
  diagnosticSchema,
  gameplayPerformanceSchema,
  type Diagnostic,
  type GameplayPerformanceReport,
} from '../../shared/diagnostics';
import { API_PATHS } from '../../shared/routes';

/** Per-page budgets; performance reports never consume the error-report allowance. */
const MAX_DIAGNOSTICS_PER_PAGE = 6;
const MAX_PERFORMANCE_REPORTS_PER_PAGE = 4;
const seen = new Set<Diagnostic['kind']>();
let sent = 0;
let performanceSent = 0;

function transmit(body: string): void {
  void fetch(API_PATHS.diagnostics, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'omit',
    referrerPolicy: 'no-referrer',
    // Small, capped reports can finish during navigation; still best-effort.
    keepalive: true,
    body,
  }).catch(() => undefined);
}

/** Separate four-window budget, so performance cannot consume error reports. */
export function reportGameplayPerformance(input: GameplayPerformanceReport): void {
  try {
    const parsed = gameplayPerformanceSchema.safeParse(input);
    if (!parsed.success || performanceSent >= MAX_PERFORMANCE_REPORTS_PER_PAGE) return;
    performanceSent++;
    transmit(JSON.stringify(parsed.data));
  } catch {
    /* Offline or unavailable telemetry must not affect play. */
  }
}
/** Per-page, finite taxonomy dedup; no identifiers, retries, storage or raw errors. */
export function reportDiagnostic(input: Diagnostic): void {
  try {
    const parsed = diagnosticSchema.safeParse(input);
    if (
      !parsed.success ||
      parsed.data.kind === 'gameplay_performance' ||
      sent >= MAX_DIAGNOSTICS_PER_PAGE ||
      seen.has(parsed.data.kind)
    )
      return;
    seen.add(parsed.data.kind);
    sent++;
    transmit(JSON.stringify(parsed.data));
  } catch {
    /* Do not recursively report reporting failures. */
  }
}

export function installDiagnostics(): void {
  window.addEventListener('error', () => reportDiagnostic({ kind: 'global_error' }));
  window.addEventListener('unhandledrejection', () => reportDiagnostic({ kind: 'unhandled_rejection' }));
}
